import { useTranslate } from "@refinedev/core";
import { Flex, Form, InputRef, Typography, UploadFile } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { validateSelectedPages } from "pages/media/utils/";
import { useEffect, useRef, useState } from "react";
import { StyledSegmentedCardWrapper } from "../styles";
import { FatButton } from "components/FatButton/FatButton/";
import { BigInput } from "components/BigInput/BigInput/";
import { LightbulbFilamentIcon } from "@phosphor-icons/react";
import { splitSelectedPagesValueByFile } from "../../helpers/project-create-wizard";

export const ALL_PAGES_VALUE = "all";

export const ProjectSettingsFormSelectedPages = <T,>({
  onChange,
  value,
  uploadedFiles = [],
}: {
  onChange: (value: T) => void;
  value?: T;
  uploadedFiles: UploadFile[];
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [includeAllPages, setIncludeAllPages] = useState(!value);
  const pagesInputRef = useRef<InputRef>(null);
  const isMultipleFilesUploaded = uploadedFiles?.length > 1;
  const allPagesValue = splitSelectedPagesValueByFile(
    typeof value === "string" ? value : undefined,
    uploadedFiles
  );

  const [uploadedFilesPages, setUploadedFilesPages] =
    useState<Record<number, string>>(allPagesValue);

  const validator = (_: unknown, value: string) => {
    // accept empty values to keep all pages when toggle is on
    if (includeAllPages || !value || value.length === 0)
      return Promise.resolve(true);

    const result = validateSelectedPages(value);
    if (result === false) {
      return Promise.reject(
        new Error(
          t("projects.components.ProjectCreateWizard.enterValidNumbers")
        )
      );
    }
    return Promise.resolve(result);
  };
  const handleChange = (value: boolean) => {
    setIncludeAllPages(value);
    if (value) {
      // all pages, send empty list of pages
      onChange(null as T);
    }
  };

  useEffect(() => {
    if (!includeAllPages) {
      setTimeout(() => pagesInputRef?.current?.focus(), 1000); // autofocus
    }
  }, [includeAllPages]);

  const options = [
    {
      value: true,
      label: t("projects.components.ProjectCreateWizard.includeAllPages"),
    },
    {
      value: false,
      label: t("projects.components.ProjectCreateWizard.specificPages"),
    },
  ];

  const handlePagesChange = (index: number, value: string) => {
    const newUploadedFilesPages: Record<number, string> = {
      ...uploadedFilesPages,
      [index]: value === "" ? ALL_PAGES_VALUE : value,
    };
    const allPages = Object.values(newUploadedFilesPages)
      .map((value) => {
        if (value === "") return ALL_PAGES_VALUE;
        return value;
      })
      .join(";");
    onChange(allPages as T);
    setUploadedFilesPages(newUploadedFilesPages);
  };

  const multipleFilesInputProps = isMultipleFilesUploaded
    ? {
        height: 40,
        fontSize: 16,
      }
    : {};

  return (
    <StyledSegmentedCardWrapper>
      <Flex vertical gap={30} style={{ height: 420 }}>
        <Flex gap={30} justify="center" style={{ marginTop: 40 }}>
          {options.map((item) => {
            return (
              <FatButton
                title={item.label}
                key={item.label}
                style={{ width: 400, height: 120 }}
                onClick={() => handleChange(item.value)}
                type={includeAllPages === item.value ? "primary" : "default"}
                checked={includeAllPages === item.value}
              />
            );
          })}
        </Flex>
        {!includeAllPages && (
          <Flex
            vertical
            gap={20}
            align="center"
            style={{ overflowY: "auto", paddingBottom: 30 }}
          >
            {uploadedFiles?.map((file, index) => (
              <Form.Item
                key={file.uid}
                initialValue={value as string}
                style={{ marginBottom: 0 }}
                name={`selectedPages-${file.uid}`}
                rules={[{ validator }]}
              >
                <div>
                  {isMultipleFilesUploaded && (
                    <Typography.Title
                      level={5}
                      type="secondary"
                      style={{ fontWeight: 400 }}
                    >
                      {file.name}
                    </Typography.Title>
                  )}
                  <BigInput
                    ref={index === 0 ? pagesInputRef : null}
                    onChange={(event) => {
                      handlePagesChange(index, event.target.value);
                    }}
                    placeholder={t(
                      "projects.components.ProjectCreateWizard.numberOfPages"
                    )}
                    value={
                      uploadedFilesPages[index] === ALL_PAGES_VALUE
                        ? ""
                        : uploadedFilesPages[index]
                    }
                    {...multipleFilesInputProps}
                  />
                </div>
              </Form.Item>
            ))}
            <Flex gap={20}>
              <LightbulbFilamentIcon size={40} color={theme.colorPrimary} />
              <Flex vertical>
                <Typography.Text type="secondary">
                  {t("projects.components.ProjectSettingForm.keepEmptyTo")}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {t(
                    "projects.components.ProjectSettingForm.example12510Numbers"
                  )}
                </Typography.Text>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </StyledSegmentedCardWrapper>
  );
};
